class ProductsIndexedDB {
    constructor() {
        this.dbName = 'GaremTechProducts';
        this.storeName = 'products';
        this.filtersStoreName = 'filters';
        this.imageCacheStateName = 'imageCacheState';
        this.productDetailsStoreName = 'productDetails';
        this.version = 3;
        this.db = null;

        // Tiempo máximo que se confía en una ficha de producto cacheada antes de
        // volver a pedirla al ERP (stock/precio pueden cambiar).
        this.productDetailTTL = 15 * 60 * 1000; // 15 minutos
    }

    async init() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);

            request.onerror = () => {
                console.error('Error opening IndexedDB');
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                console.log('IndexedDB initialized');
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                // v3: recreamos el store de productos SIN keyPath propio. Con
                // keyPath:'id' + add(), un listado con id null/repetido (habitual:
                // las filas de listado no traen un 'id' único, ver product.modelo_id
                // || product.id en la plantilla) abortaba TODA la transacción al
                // primer duplicado, dejando IndexedDB vacío aunque el servidor sí
                // devolviera productos. Con autoIncrement no puede haber colisión.
                if (db.objectStoreNames.contains(this.storeName)) {
                    db.deleteObjectStore(this.storeName);
                }
                const productStore = db.createObjectStore(this.storeName, { autoIncrement: true });
                productStore.createIndex('coleccion_id', 'coleccion_id', { unique: false });
                productStore.createIndex('seccion_id', 'seccion_id', { unique: false });
                productStore.createIndex('familia_id', 'familia_id', { unique: false });

                // Crear object store para filtros
                if (!db.objectStoreNames.contains(this.filtersStoreName)) {
                    db.createObjectStore(this.filtersStoreName, { keyPath: 'key' });
                }

                // Crear object store para estado de precacheo de imágenes
                if (!db.objectStoreNames.contains(this.imageCacheStateName)) {
                    db.createObjectStore(this.imageCacheStateName, { keyPath: 'seasonId' });
                }

                // Crear object store para fichas de producto individuales (caché de la ficha)
                if (!db.objectStoreNames.contains(this.productDetailsStoreName)) {
                    db.createObjectStore(this.productDetailsStoreName, { keyPath: 'key' });
                }
            };
        });
    }

    async saveProducts(products, filters, metadata = {}) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const tx = this.db.transaction([this.storeName, this.filtersStoreName], 'readwrite');

            // Guardar productos
            const productStore = tx.objectStore(this.storeName);
            productStore.clear();

            products.forEach((product, index) => {
                productStore.add({ ...product, ord: index + 1 });
            });

            // Guardar filtros
            const filterStore = tx.objectStore(this.filtersStoreName);
            filterStore.clear();
            filterStore.add({ key: 'filters', data: filters, timestamp: Date.now(), ...metadata });

            tx.oncomplete = () => {
                console.log(`Saved ${products.length} products to IndexedDB`);
                resolve();
            };

            tx.onerror = () => {
                console.error('Error saving to IndexedDB:', tx.error);
                reject(tx.error);
            };
        });
    }

    async getProducts() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const tx = this.db.transaction([this.storeName], 'readonly');
            const store = tx.objectStore(this.storeName);
            const request = store.getAll();

            request.onsuccess = () => {
                resolve(request.result || []);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    async getFilters() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const tx = this.db.transaction([this.filtersStoreName], 'readonly');
            const store = tx.objectStore(this.filtersStoreName);
            const request = store.get('filters');

            request.onsuccess = () => {
                resolve(request.result || null);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    async filterProducts(filterCriteria, myProducts = false) {
        let products = await this.getProducts();

        // Filtrar solo productos con precio > 0 si myProducts está activo
        if (myProducts) {
            products = products.filter(product => {
                const hasValidPrice = product.price != null && product.price !== '' && parseFloat(product.price) > 0;
                return hasValidPrice;
            });
        }

        if (Object.keys(filterCriteria).length === 0) {
            return products;
        }

        // Precompilar los filtros una sola vez
        const compiledFilters = {};
        for (const [key, values] of Object.entries(filterCriteria)) {
            if (!values || values.length === 0) continue;
            // Usar Set para búsquedas O(1)
            compiledFilters[key] = new Set(values.map(v => v.id ? v.id.toString() : v.toString()));
        }

        const filterKeys = Object.keys(compiledFilters);
        if (filterKeys.length === 0) return products;

        // Filtrado optimizado
        return products.filter(product => {
            for (const key of filterKeys) {
                const valueSet = compiledFilters[key];

                // Para arrays en el producto
                if (Array.isArray(product[key])) {
                    let hasMatch = false;
                    for (const item of product[key]) {
                        if (valueSet.has((item.id || item).toString())) {
                            hasMatch = true;
                            break;
                        }
                    }
                    if (!hasMatch) return false;
                } else {
                    // Para valores únicos
                    const productValue = (product[key] || '').toString();
                    if (!valueSet.has(productValue)) return false;
                }
            }
            return true;
        });
    }

    async isImagesCached(seasonId) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const tx = this.db.transaction([this.imageCacheStateName], 'readonly');
            const store = tx.objectStore(this.imageCacheStateName);
            const request = store.get(seasonId);

            request.onsuccess = () => {
                const result = request.result;
                resolve(result && result.cached === true);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    async markImagesCached(seasonId) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const tx = this.db.transaction([this.imageCacheStateName], 'readwrite');
            const store = tx.objectStore(this.imageCacheStateName);
            const request = store.put({
                seasonId: seasonId,
                cached: true,
                timestamp: Date.now()
            });

            request.onsuccess = () => {
                console.log(`Marked season ${seasonId} as cached`);
                resolve();
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    async clear() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const tx = this.db.transaction([this.storeName, this.filtersStoreName, this.imageCacheStateName], 'readwrite');
            tx.objectStore(this.storeName).clear();
            tx.objectStore(this.filtersStoreName).clear();
            tx.objectStore(this.imageCacheStateName).clear();

            tx.oncomplete = () => {
                console.log('IndexedDB cleared');
                resolve();
            };

            tx.onerror = () => {
                reject(tx.error);
            };
        });
    }

    /**
     * Busca en la caché del LISTADO (store `products`) un resumen del modelo
     * (nombre/imagen/precio) para pintar una vista previa instantánea de la
     * ficha mientras se pide el detalle completo al ERP. No es un índice
     * (el store está indexado por `id` de artículo, no por `modelo_id`), así
     * que se hace un recorrido en memoria sobre lo ya cargado del listado.
     */
    async getProductPreview(modeloId) {
        const products = await this.getProducts();
        const match = products.find(p => String(p.modelo_id) === String(modeloId));
        if (!match) return null;

        return {
            name: match.name || '',
            img: match.img || '',
            price: match.price || 0,
            discount: match.discount || 0,
            srp: match.srp || 0,
            moneda: match.moneda || '€',
            coleccion: match.coleccion || '',
            familia: match.familia || '',
            temporada_id: match.temporada_id || null,
            colores: Array.isArray(match.colores) ? match.colores : [],
        };
    }

    /**
     * Devuelve la ficha de producto cacheada para `key` si existe y no ha
     * caducado (productDetailTTL), o null en caso contrario.
     */
    async getProductDetail(key) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const tx = this.db.transaction([this.productDetailsStoreName], 'readonly');
            const store = tx.objectStore(this.productDetailsStoreName);
            const request = store.get(key);

            request.onsuccess = () => {
                const result = request.result;
                if (!result) {
                    resolve(null);
                    return;
                }
                const isExpired = (Date.now() - result.timestamp) > this.productDetailTTL;
                resolve(isExpired ? null : result);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    /**
     * Guarda (o sobreescribe) la ficha de producto para `key`.
     */
    async saveProductDetail(key, products) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const tx = this.db.transaction([this.productDetailsStoreName], 'readwrite');
            const store = tx.objectStore(this.productDetailsStoreName);
            store.put({ key, products, timestamp: Date.now() });

            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
}

// Exportar la clase globalmente
window.ProductsIndexedDB = ProductsIndexedDB;
